import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import useMediaQuery from '@mui/material/useMediaQuery';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTheme } from '@mui/material/styles';
import { AdvancedPointerView } from '..';
import mixpanel from 'mixpanel-browser';
import { Listing, OccupancyRate, ADR, Revenue, ListingLegacy } from 'src/interfaces/idashboard';
import { IndividualListingModalPropTypes } from 'src/interfaces/iproptypes';
import PerformanceData from 'src/App/Components/PerformanceData';
import TopBar from './TopBar';
import HeroTitleSection from './HeroTitleSection';
import BarGraphData from './BarGraphData';
import Ameneties from './Ameneties';
import styles from './style';
import { processManagerWithId, getMonthlyKeyMetrics, isListingLegacy } from 'src/utils/functions';
import { useDispatch } from 'react-redux';
import DisabledBlurCard from 'src/App/Pages/EstimatorPage/UtilityComponents/BlurCard';
import HistoricalPerformanceGraph from './HistoricalPerformanceGraph';

mixpanel.init('0c9d3451de8e0a1a2f21ec23b05c2063');

const IndividualListingModal = ({
  open,
  onClose,
  data,
  currency,
  isDisabled,
  listings,
  subscriptionModalOpen,
  setSubscriptionModalOpen,
  handleOnSampleClick,
  multipleBookingMarkersCallLoading,
  estimatorSampleListingData,
  isEstimator,
  openDemoBookingForm,
  setDemoFormHeadingFor,
  isHistoricalDataHidden,
  onEstimatorLockedMarkerHistoricalDataClick,
}: IndividualListingModalPropTypes) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  const [_, setTooltipOpen] = useState<boolean>(false);
  const [advancedViewOpen, setAdvancedViewOpen] = useState<boolean>(false);
  const [ownerTotalListings, setOwnerTotalListings] = useState<Listing[] | ListingLegacy[]>([]);

  const [dynamicOccupancyRate, setDynamicOccupancyRate] = useState<OccupancyRate[]>([]);
  const [dynamicADR, setDynamicADR] = useState<ADR[]>([]);
  const [dynamicRev, setDynamicRev] = useState<Revenue[]>([]);

  const handleAdvancedViewAction = () => {
    const _ownerTotalListings = processManagerWithId(listings ? listings : [], data['hostId']);
    setOwnerTotalListings(_ownerTotalListings);
    if (!isDisabled) {
      setDynamicOccupancyRate(
        getMonthlyKeyMetrics('occupancy', _ownerTotalListings) as OccupancyRate[]
      );
      setDynamicADR(getMonthlyKeyMetrics('adr', _ownerTotalListings) as ADR[]);
      setDynamicRev(getMonthlyKeyMetrics('revenue', _ownerTotalListings, false, true) as Revenue[]);
    }
  };

  const [historicalPerfTrigger, setHistoricalPerfTrigger] = useState(false);
  const triggerHistoricalPerf = () => {
    if (!historicalPerfTrigger) {
      mixpanel.track('seeHistoricalPerformance');
    }
    handleAdvancedViewAction();
    setAdvancedViewOpen(true);
    setHistoricalPerfTrigger(!historicalPerfTrigger);
  };

  useEffect(() => {
    if (subscriptionModalOpen) {
      onClose();
    }
  }, [subscriptionModalOpen]);

  //hide tawkbot
  const _window: any = window;

  const showWidget = () => {
    _window.Tawk_API && _window.Tawk_API.showWidget && _window.Tawk_API.showWidget();
  };

  const hideWidget = () => {
    _window.Tawk_API && _window.Tawk_API.hideWidget && _window.Tawk_API.hideWidget();
  };

  useEffect(() => {
    if (open && _window.Tawk_API) {
      hideWidget();
    }
    return () => {
      showWidget();
    };
  }, [open]);

  const thumbnailUrl = estimatorSampleListingData
    ? estimatorSampleListingData.thumbnail_url
    : data['thumbnail_url'];

  const listingUrl = estimatorSampleListingData
    ? estimatorSampleListingData.listing_url
    : `airbnb.com/rooms/${data['listingID']}`;

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            ...styles.background,
            height: isMobileScreen ? '100vh' : '85vh',
            width: isMobileScreen ? '100vw' : '90vw',
            display: 'flex',
            justifyContent: 'center',
            outline: 'none !important',
          }}
        >
          <Grid
            justifyContent={isMobileScreen ? 'center' : 'flex-start'}
            container
            style={{ width: '100%' }}
            xs={12}
          >
            <TopBar onClose={onClose} isMobileScreen={isMobileScreen} data={data} />

            <Grid
              justifyContent={'center'}
              direction="row"
              container
              style={{
                width: isMobileScreen ? '98%' : '100%',
              }}
              spacing={4}
              xs={12}
            >
              <Grid
                container
                direction="column"
                item
                md={7}
                sm={12}
                xs={12}
                order={{ md: 1, sm: 2, xs: 2 }}
              >
                <HeroTitleSection
                  data={data}
                  // estimatorSampleListingData={estimatorSampleListingData}
                />
                <Grid item style={{ marginTop: '10px' }}>
                  <PerformanceData
                    data={data}
                    currency={currency}
                    // estimatorSampleListingData={estimatorSampleListingData}
                    handleOnSampleClick={handleOnSampleClick}
                    isDisabled={isDisabled}
                    isEstimator={isEstimator}
                    isListingRecentlyAdded={data['annualRevenueLTM'] == 'N/A'}
                  />
                </Grid>
              </Grid>
              <Grid
                item
                md={5}
                sm={12}
                xs={12}
                order={{ md: 2, sm: 1, xs: 1 }}
                style={{ marginBottom: 0 }}
                justifyContent="flex-start"
              >
                <Link href={'https://www.' + listingUrl} target="_blank" rel="nofollow">
                  <img
                    src={thumbnailUrl.split('?')[0]}
                    style={{ width: '100%', float: 'left', height: '250px', objectFit: 'cover' }}
                  />
                </Link>
              </Grid>
            </Grid>

            <Grid
              container
              direction="row"
              justifyContent={isMobileScreen ? 'center' : 'flex-start'}
              alignItems="flex-start"
              spacing={4}
              sx={{ width: '100%', marginTop: 0 }}
              item
            >
              {/* {multipleBookingMarkersCallLoading ? (
                <Grid item md={7} sm={12} xs={12}>
                  <CircularProgress size={60} />
                </Grid>
              ) : (
                <Grid item md={7} sm={12} xs={12}>
                  <BarGraphData
                    data={data}
                    isDisabled={isDisabled}
                    estimatorSampleListingData={estimatorSampleListingData}
                    currency={currency}
                  />
                </Grid>
              )} */}

              <Grid item md={7} sm={12} xs={12}>
                {data['markerType'] && data['markerType'] === 'full' && (
                  <>
                    <BarGraphData
                      data={data}
                      isDisabled={isDisabled}
                      // estimatorSampleListingData={estimatorSampleListingData}
                      currency={currency}
                    />
                  </>
                )}
                {!isEstimator && (
                  <HistoricalPerformanceGraph
                    listingID={data['listingID']}
                    handleOnSampleClick={handleOnSampleClick}
                    currency={currency}
                  />
                )}
              </Grid>

              <Grid item md={5} sm={12} xs={12}>
                <Grid
                  container
                  direction="column"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                >
                  <Grid
                    item
                    style={{
                      width: '100%',
                    }}
                  >
                    <Ameneties
                      data={data}
                      isMobileScreen={isMobileScreen}
                      // estimatorSampleListingData={estimatorSampleListingData}
                    />
                  </Grid>
                  {/* Advanced view buttons */}

                  {isEstimator && (
                    <Grid
                      container
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                      style={{ width: '100%', position: 'relative' }}
                      xs={12}
                    >
                      {isHistoricalDataHidden && (
                        <DisabledBlurCard
                          size={20}
                          subscriptionType="Expert"
                          onDisabledOptionClick={() => {
                            onClose();
                            onEstimatorLockedMarkerHistoricalDataClick();
                          }}
                        />
                      )}
                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={6}
                        style={{
                          width: isMobileScreen ? '100%' : 'auto',
                        }}
                      >
                        <Button
                          color="primary"
                          onClick={() => {
                            handleAdvancedViewAction();
                            setAdvancedViewOpen(true);
                          }}
                          sx={styles.button}
                          disabled={isHistoricalDataHidden}
                        >
                          Host Insights <OpenInNewIcon style={{ marginLeft: '10px' }} />
                        </Button>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sm={12}
                        md={6}
                        style={{
                          width: isMobileScreen ? '100%' : 'auto',
                          marginBottom: isMobileScreen ? '20px' : 0,
                        }}
                      >
                        <Button
                          color="primary"
                          onClick={triggerHistoricalPerf}
                          sx={styles.button}
                          disabled={isHistoricalDataHidden}
                        >
                          Historical Performance <OpenInNewIcon style={{ marginLeft: '10px' }} />
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      {advancedViewOpen && (
        <AdvancedPointerView
          advancedViewOpen={advancedViewOpen}
          setAdvancedViewOpen={setAdvancedViewOpen}
          data={data}
          historicalPerfTrigger={historicalPerfTrigger}
          setHistoricalPerfTrigger={setHistoricalPerfTrigger}
          setSubscriptionModalOpen={setSubscriptionModalOpen}
          dynamicOccupancyRate={dynamicOccupancyRate}
          dynamicRev={dynamicRev}
          dynamicADR={dynamicADR}
          getMonthlyKeyMetrics={getMonthlyKeyMetrics}
          ownerTotalListings={ownerTotalListings as ListingLegacy[]}
          currency={currency}
          isDisabled={isDisabled ? isDisabled : false}
          handleOnSampleClick={handleOnSampleClick}
          setTooltipOpen={setTooltipOpen}
          // estimatorSampleListingData={estimatorSampleListingData}
        />
      )}
    </>
  );
};

export default IndividualListingModal;
